'use client'

import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'
import Link from 'next/link'

interface BridgeGridData {
	title?: string
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				collection: string
				item: {
					title?: string
					text?: string
					image?: {
						id: string
						filename_disk: string
						filename_download: string
						description?: string
					}
					url?: string
					target?: string
				}
			}[]
		}
	}[]
}

export const fields = [
	'title',
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.collection',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'content_collection.site_content_collections_id.collection.item:site_content.image.id',
	'content_collection.site_content_collections_id.collection.item:site_content.image.filename_disk',
	'content_collection.site_content_collections_id.collection.item:site_content.image.filename_download',
	'content_collection.site_content_collections_id.collection.item:site_content.image.description',
	'content_collection.site_content_collections_id.collection.item:external_links.title',
	'content_collection.site_content_collections_id.collection.item:external_links.url',
	'content_collection.site_content_collections_id.collection.item:external_links.target',
] as const

interface BridgeGridProps {
	data?: BridgeGridData
}

export default function BridgeGrid({ data }: BridgeGridProps) {
	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})

	const cards =
		data?.content_collection?.map((collectionItem) => {
			const items = collectionItem.site_content_collections_id?.collection ?? []

			const titleItem = items.find((i) => i.collection === 'site_content' && i.item.title)?.item
			const textItem = items.find((i) => i.collection === 'site_content' && i.item.text)?.item
			const imageItem = items.find((i) => i.collection === 'site_content' && i.item.image?.id)?.item
			const linkItem = items.find((i) => i.collection === 'external_links')?.item

			return {
				id: collectionItem.id,
				title: titleItem?.title ?? '',
				text: textItem?.text ?? '',
				image: imageItem?.image,
				linkUrl: linkItem?.url ?? '',
				linkLabel: linkItem?.title ?? '',
				linkTarget: linkItem?.target ?? '_self',
			}
		}) ?? []

	return (
		<section
			data-name='BridgeGrid'
			ref={ref}
			className='BridgeGrid py-12 lg:py-16 overflow-hidden'
		>
			<div className='container'>

				<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8'>
					{cards.map((card, index) => {
						const imageUrl = card.image?.id
							? getReadableImageUrl(
									card.image.id,
									card.image.filename_disk || card.image.filename_download
							  )
							: ''
						const imageAlt = card.image?.description || card.title || ''

						const cardContent = (
							<>
								<div className='relative w-full pb-[100%] overflow-hidden group'>
									{imageUrl && (
										<Image
											src={imageUrl}
											alt={imageAlt}
											fill
											className='object-cover transition-transform duration-500 group-hover:scale-105'
											sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
										/>
									)}

									<div className='absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-all duration-300' />

									{card.title && (
										<div className='absolute bottom-0 left-0 right-0 bg-black/40 px-4 py-3 transition-all duration-300 group-hover:opacity-0'>
											<p className='text-white text-lg lg:text-xl font-light tracking-wide'>
												{card.title}
											</p>
										</div>
									)}

									{card.linkUrl && (
										<div className='absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300'>
											<p className='text-white text-lg lg:text-xl font-light tracking-wide text-center px-4'>
												{card.title}
											</p>
											<span className='mt-2 px-6 py-2 border border-white text-white text-sm tracking-widest uppercase hover:bg-white hover:text-black transition-colors duration-200'>
												{card.linkLabel}
											</span>
										</div>
									)}
								</div>

								{card.text && (
									<div
										className='[&>p]:text-sm [&>p]:leading-relaxed [&>p]:text-secondary-700'
										dangerouslySetInnerHTML={sanitizeHTML(card.text)}
									/>
								)}
							</>
						)

						return (
							<FadeIn
								key={card.id}
								isVisible={isVisible}
								delay={200 + index * 80}
								direction='up'
								className='flex flex-col gap-4'
							>
								{card.linkUrl ? (
									<Link
										href={card.linkUrl}
										target={card.linkTarget}
										className='flex flex-col gap-4'
									>
										{cardContent}
									</Link>
								) : (
									cardContent
								)}
							</FadeIn>
						)
					})}
				</div>
			</div>
		</section>
	)
}
